import React, { useState, useContext, useEffect, useId, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataContext } from "../../../../context/DataContext";
import { tienePermiso } from "../../../../helpers/permisos";
import { getToken } from "../../../../api/Account";
import { ConfirmDialog, EstadoError, useAviso } from "../../../../components/base";
import { formatearMonto } from "../../../../helpers/formato";
import {
  addQuoteAsync,
  addQuoteNominalAsync,
  getQuoteSugestAsync,
  getSaldoAnteriorAsync,
  liquidateCreditAsync,
} from "../../../../api/Credits";

// Texto del error del monto, o "" si es válido. Los errores de campo vacío o no numérico
// (el "." suelto) solo salen cuando el usuario ya tocó el campo (`tocado`); el de "mayor al saldo" sale al escribir,
// como el aviso que antes saltaba en cada tecla. El 0 se acepta, como en HEAD (solo se rechazaba el vacío);
// los negativos no se pueden escribir (la expresión regular no deja el "-").
// En dólares solo se puede comparar con el saldo "tal cual": el tipo de cambio no está cargado en
// este modal. Como un dólar vale más de un córdoba, si el número ya supera el saldo en córdobas
// el abono seguro lo supera (es el mismo tope que había, sin recortar lo escrito).
const errorDelMonto = ({ monto, saldo, cargado, tocado, moneda }) => {
  if (monto === "") return tocado ? "Ingrese el monto a abonar" : "";
  const valor = parseFloat(monto);
  if (Number.isNaN(valor)) return tocado ? "Ingrese un monto válido" : "";
  if (cargado && valor > saldo) {
    return moneda === 1
      ? `El abono no puede superar el saldo del crédito (${formatearMonto(saldo)})`
      : `El monto en dólares supera el saldo del crédito (${formatearMonto(saldo)})`;
  }
  return "";
};

// Un dato del resumen: etiqueta pequeña y cifra tabular. `cargando` pinta un esqueleto en vez de "C$0.00".
const DatoResumen = ({ etiqueta, valor, cargando }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {etiqueta}
    </Typography>
    {cargando ? (
      <Skeleton
        variant="text"
        width={110}
        sx={{
          fontSize: "1.25rem",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    ) : (
      <Typography variant="monto" component="p" sx={{ fontSize: "1.25rem" }}>
        {valor}
      </Typography>
    )}
  </Box>
);

const AddAbono = (props) => {
  // `abierto` es opcional. Si el padre lo pasa, el cobro se dibuja en su propio diálogo (que no se
  // cierra con un clic fuera si ya hay un monto escrito, ni mientras guarda). Si no lo pasa, el
  // padre lo envuelve en su propio modal y aquí solo se dibuja el contenido.
  if (props.abierto === false) return null;
  return <CobroAbono {...props} />;
};

const CobroAbono = ({
  setShowModal,
  id,
  localReload,
  setLocalReload,
  isNominal,
  abierto,
  cliente,
}) => {
  const [montoAbono, setMontoAbono] = useState("");
  const [concepto, setConcepto] = useState("");
  const [active, setActive] = useState(1);
  const [quoteSugest, setquoteSugest] = useState(0);
  const [totalAbonado, setTotalAbonado] = useState(0);
  const [isLiquidate, setIsLiquidate] = useState(false);

  const [tipoAbono, setTipoAbono] = useState(1);
  // El overlay de carga no bloquea el teclado: un segundo Enter sobre el botón mandaba otro
  // abono. El ref corta el segundo envío al instante; el estado deshabilita el botón.
  const enviando = useRef(false);
  const [guardando, setGuardando] = useState(false);
  // Carga de los dos saldos: mientras no llegan, no se muestran cifras en 0 ni se puede abonar.
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [tocado, setTocado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const idMoneda = useId();
  const idTipo = useId();
  const aviso = useAviso();
  const conMarco = abierto !== undefined;

  const token = getToken();
  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);

  // Liquidar: CREDIT LIQUIDATE es el permiso que el rol decide dar, pero el servidor (LiquidateCredit)
  // exige CREDITS CREATE y responde 401 sin él. Se piden los dos para no ofrecer un botón que cierra la sesión.
  // `tienePermiso` no lanza si falta la fila del permiso: sin permiso simplemente no se ofrece liquidar.
  const puedeLiquidar =
    tienePermiso(access, "CREDIT LIQUIDATE") && tienePermiso(access, "CREDITS CREATE");

  const cargarDatos = async () => {
    setCargando(true);
    setErrorCarga(false);
    setIsLoading(true);
    const result = await getQuoteSugestAsync(token, id);
    if (!result.statusResponse) {
      setIsLoading(false);
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      setCargando(false);
      setErrorCarga(true);
      return;
    }
    setIsLoading(false);
    setquoteSugest(result.data.result);
    getSaldoAnterior();
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const getSaldoAnterior = async () => {
    const result = await getSaldoAnteriorAsync(token, id);
    if (!result.statusResponse) {
      setIsLoading(false);
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      setCargando(false);
      setErrorCarga(true);
      return;
    }
    setIsLoading(false);
    setTotalAbonado(result.data.result);
    setCargando(false);
  };

  //Devuelve un entero positivo
  const funtionSetMontoCuota = (value) => {
    if (/^\d*\.?\d*$/.test(value.toString())) {
      setMontoAbono(value);
    }
  };

  const enDolares = active === 2;
  const valorMonto = montoAbono === "" ? 0 : parseFloat(montoAbono);
  const errorMonto = errorDelMonto({
    monto: montoAbono,
    saldo: totalAbonado,
    cargado: !cargando && !errorCarga,
    tocado,
    moneda: active,
  });
  // Nuevo saldo: igual que antes (saldo menos el monto), solo en córdobas. En dólares el backend
  // multiplica por el tipo de cambio del día, que aquí no se conoce: no se inventa la conversión.
  const nuevoSaldo = totalAbonado - valorMonto;
  const nuevoSaldoValido = !errorMonto && !Number.isNaN(nuevoSaldo) && nuevoSaldo >= 0;
  // Con "liquidar" activo se llama al endpoint de liquidar solo si el crédito no es nominal
  // (el endpoint nominal tiene prioridad, como siempre).
  const liquidaAhora = !isNominal && isLiquidate;

  const enviar = async () => {
    if (enviando.current) {
      return;
    }
    enviando.current = true;
    setGuardando(true);
    setIsLoading(true);
    const data = {
      creditId: id,
      totalRecibido: montoAbono,
      // OJO: tipoAbono es un número (1 o 2) y aquí se compara con la cadena "1": nunca se cumple y
      // siempre se guarda "ABONO". Es un fallo funcional que se propone aparte; no se arregla aquí.
      concepto: tipoAbono === "1" ? "ABONO DEL DIA" : "ABONO",
      moneda: active,
    };

    setIsLoading(true);
    const result = isNominal
      ? await addQuoteNominalAsync(token, data)
      : isLiquidate
      ? await liquidateCreditAsync(token, data)
      : await addQuoteAsync(token, data);
    enviando.current = false;
    setGuardando(false);
    if (!result.statusResponse) {
      setIsLoading(false);
      setConfirmando(false);
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      aviso.error(result.error || "No se pudo aplicar el abono");
      return;
    }
    setIsLoading(false);
    aviso.exito("Abono aplicado");
    setLocalReload(!localReload);
    setShowModal(false);
  };

  // Botón principal: valida junto al campo; con "liquidar" pide confirmación antes de enviar.
  const aplicarAbono = () => {
    if (enviando.current) {
      return;
    }
    setTocado(true);
    if (cargando || errorCarga) return;
    const error = errorDelMonto({
      monto: montoAbono,
      saldo: totalAbonado,
      cargado: true,
      tocado: true,
      moneda: active,
    });
    if (error) return;
    if (liquidaAhora) {
      setConfirmando(true);
      return;
    }
    enviar();
  };

  const onChangeTipoAbono = (tipo) => {
    setTipoAbono(tipo);
    setConcepto(tipo === 1 ? "ABONO DEL DIA" : "ABONO");
  };

  const cerrar = (_evento, razon) => {
    // Mientras guarda no se cierra; un clic fuera no debe perder un monto ya escrito.
    if (guardando || confirmando) return;
    if ((razon === "backdropClick" || razon === "escapeKeyDown") && montoAbono !== "") return;
    setShowModal(false);
  };

  const mensajeLiquidar = (
    <>
      Se liquidará el crédito #{id} por{" "}
      <strong>
        {enDolares ? formatearMonto(valorMonto, "$") : formatearMonto(valorMonto)}
      </strong>
      .{" "}
      {enDolares || !nuevoSaldoValido
        ? "El saldo que quede se registra como pérdida financiera y el crédito queda cancelado."
        : `El saldo que quede (${formatearMonto(
            nuevoSaldo
          )}) se registra como pérdida financiera y el crédito queda cancelado.`}
    </>
  );

  const cuerpo = errorCarga ? (
    <EstadoError
      titulo="No se pudo cargar el saldo del crédito"
      mensaje="Sin el saldo no se puede abonar. Revisa tu conexión e inténtalo de nuevo."
      onReintentar={cargarDatos}
    />
  ) : (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2.5,
          bgcolor: (theme) => theme.palette.soft.primary,
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {cliente ? `Crédito #${id}` : "Cobro del crédito"}
        </Typography>
        <Typography
          component="p"
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main", overflowWrap: "anywhere" }}
        >
          {cliente || `Crédito #${id}`}
        </Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 4 }}>
          <DatoResumen
            etiqueta="Saldo actual"
            valor={formatearMonto(totalAbonado)}
            cargando={cargando}
          />
          <DatoResumen
            etiqueta="Para ponerse al día"
            valor={formatearMonto(quoteSugest)}
            cargando={cargando}
          />
        </Box>
      </Box>

      <TextField
        select
        fullWidth
        id={idMoneda}
        label="Moneda"
        value={active}
        onChange={(e) => setActive(e.target.value)}
        disabled={guardando}
      >
        <MenuItem value={1}>Córdoba (C$)</MenuItem>
        <MenuItem value={2}>Dólar ($)</MenuItem>
      </TextField>

      <TextField
        required
        fullWidth
        autoFocus
        label="Monto a abonar"
        value={montoAbono}
        onChange={(e) => funtionSetMontoCuota(e.target.value)}
        onBlur={() => setTocado(true)}
        error={Boolean(errorMonto)}
        helperText={errorMonto || " "}
        disabled={guardando}
        inputProps={{ inputMode: "decimal", autoComplete: "off" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{enDolares ? "$" : "C$"}</InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          p: 2,
          borderRadius: 2.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          component="h3"
          variant="body2"
          sx={{ fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          Después de este abono
        </Typography>
        {enDolares ? (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <InfoOutlinedIcon
              aria-hidden="true"
              fontSize="small"
              sx={{ color: "primary.main", mt: 0.25 }}
            />
            <Typography variant="body2">
              El saldo se calculará con el tipo de cambio del día.
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            <DatoResumen
              etiqueta="Abono"
              valor={formatearMonto(montoAbono === "" ? null : valorMonto)}
              cargando={false}
            />
            <DatoResumen
              etiqueta="Nuevo saldo"
              valor={nuevoSaldoValido ? formatearMonto(nuevoSaldo) : "—"}
              cargando={cargando}
            />
          </Box>
        )}
      </Box>

      <TextField
        select
        fullWidth
        id={idTipo}
        label="Tipo de abono"
        value={tipoAbono}
        onChange={(e) => onChangeTipoAbono(e.target.value)}
        disabled={guardando}
      >
        <MenuItem value={1}>Abono del día</MenuItem>
        <MenuItem value={2}>Abono</MenuItem>
      </TextField>

      {puedeLiquidar && !isNominal ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isLiquidate}
                onChange={() => setIsLiquidate(!isLiquidate)}
                disabled={guardando}
              />
            }
            label="Liquidar el crédito con este abono"
            sx={{ m: 0, gap: 1, fontWeight: 700 }}
          />
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            El crédito queda cancelado y el saldo que quede se registra como pérdida
            financiera.
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );

  const acciones = (
    <>
      <Button onClick={() => cerrar()} disabled={guardando}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        size="large"
        disabled={guardando || cargando || errorCarga}
        onClick={aplicarAbono}
        startIcon={
          guardando ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {guardando ? "Aplicando…" : "Aplicar abono"}
      </Button>
    </>
  );

  const confirmacion = (
    <ConfirmDialog
      abierto={confirmando}
      titulo="Liquidar crédito"
      mensaje={mensajeLiquidar}
      textoConfirmar="Liquidar crédito"
      peligro
      cargando={guardando}
      onConfirmar={enviar}
      onCancelar={() => setConfirmando(false)}
    />
  );

  if (!conMarco) {
    return (
      <>
        <Box sx={{ pt: 1 }}>{cuerpo}</Box>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          {acciones}
        </Stack>
        {confirmacion}
      </>
    );
  }

  return (
    <>
      <Dialog
        open
        onClose={cerrar}
        fullWidth
        maxWidth="sm"
        aria-labelledby={`${idMoneda}-titulo`}
        sx={{
          "& .MuiDialog-paper": {
            m: { xs: 1.5, sm: 4 },
            maxWidth: { xs: "calc(100% - 24px)", sm: 560 },
          },
        }}
      >
        <DialogTitle
          id={`${idMoneda}-titulo`}
          component="h2"
          sx={{ fontWeight: 700, color: "primary.main", pr: 7 }}
        >
          Aplicar abono
        </DialogTitle>
        <IconButton
          aria-label="Cerrar"
          onClick={() => cerrar()}
          disabled={guardando}
          sx={{
            position: "absolute",
            top: (theme) => theme.spacing(1.5),
            right: (theme) => theme.spacing(1.5),
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>{cuerpo}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
      </Dialog>
      {confirmacion}
    </>
  );
};

export default AddAbono;
